import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { HelpCircle, X } from 'lucide-react';

export function MbBadge({
  children,
  tone = 'muted',
}: {
  children: ReactNode;
  tone?: 'muted' | 'brand' | 'success' | 'warn' | 'danger' | 'purple';
}) {
  const tones: Record<string, string> = {
    muted: 'bg-[var(--dash-soft)] text-[var(--dash-muted)]',
    brand: 'bg-[var(--dash-brand-soft)] text-[var(--dash-brand)]',
    success: 'bg-[var(--dash-success-soft)] text-[var(--dash-success)]',
    warn: 'bg-amber-50 text-amber-800',
    danger: 'bg-red-50 text-red-700',
    purple: 'bg-[var(--dash-accent-soft)] text-[var(--dash-accent)]',
  };
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-bold tracking-wide uppercase ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

export function MbSegmentedControl<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
}: {
  value: T;
  onChange: (next: T) => void;
  options: Array<{ value: T; label: string; hint?: string }>;
  ariaLabel: string;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className="mb-seg inline-flex w-full rounded-xl border border-[var(--dash-line)] bg-[var(--dash-soft)] p-1"
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            title={opt.hint}
            onClick={() => onChange(opt.value)}
            className={`mb-seg__btn flex-1 rounded-lg px-3 py-2 text-xs font-semibold transition ${
              active
                ? 'bg-white text-[var(--dash-ink)] shadow-sm ring-1 ring-[var(--dash-line)]'
                : 'text-[var(--dash-muted)] hover:text-[var(--dash-ink)]'
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

type TooltipPos = CSSProperties;

function buildTooltipStyle(anchor: HTMLElement): TooltipPos {
  const rect = anchor.getBoundingClientRect();
  const pad = 10;
  const width = 240;
  let left = Math.min(
    Math.max(pad, rect.left + rect.width / 2 - width / 2),
    window.innerWidth - width - pad,
  );
  const spaceAbove = rect.top - pad;
  const placeAbove = spaceAbove > 80;
  return {
    position: 'fixed',
    left,
    width,
    zIndex: 600,
    ...(placeAbove
      ? { bottom: window.innerHeight - rect.top + 8 }
      : { top: rect.bottom + 8 }),
  };
}

/** Accessible help tooltip — portal + collision-aware; works without hover on focus. */
export function MbHelpTooltip({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const tipId = useId();
  const [style, setStyle] = useState<TooltipPos | null>(null);

  useLayoutEffect(() => {
    if (!open || !btnRef.current) {
      setStyle(null);
      return;
    }
    setStyle(buildTooltipStyle(btnRef.current));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || tipRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const tip =
    open && style && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={tipRef}
            id={tipId}
            role="tooltip"
            style={style}
            className="mb-tooltip rounded-xl border border-[var(--dash-line)] bg-[var(--dash-ink)] px-3 py-2.5 text-xs leading-relaxed text-white shadow-lg"
          >
            {children}
          </div>,
          document.body,
        )
      : null;

  return (
    <span className="inline-flex">
      <button
        ref={btnRef}
        type="button"
        className="inline-flex h-6 w-6 items-center justify-center rounded-full text-[var(--dash-muted)] transition hover:bg-[var(--dash-soft)] hover:text-[var(--dash-ink)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--dash-brand)]"
        aria-label={label}
        aria-describedby={open ? tipId : undefined}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onBlur={() => {
          // Delay so clicks inside tip register
          window.setTimeout(() => {
            if (!tipRef.current?.matches(':hover')) setOpen(false);
          }, 120);
        }}
      >
        <HelpCircle className="h-3.5 w-3.5" aria-hidden />
      </button>
      {tip}
    </span>
  );
}

export function MbAlert({
  tone = 'info',
  title,
  children,
  actions,
  onDismiss,
}: {
  tone?: 'info' | 'success' | 'warn' | 'danger';
  title: string;
  children?: ReactNode;
  actions?: ReactNode;
  onDismiss?: () => void;
}) {
  const map = {
    info: 'border-blue-200 bg-blue-50/80 text-blue-950',
    success: 'border-emerald-200 bg-emerald-50/80 text-emerald-950',
    warn: 'border-amber-200 bg-amber-50 text-amber-950',
    danger: 'border-red-200 bg-red-50 text-red-950',
  };
  const icon = {
    info: 'i',
    success: '✓',
    warn: '!',
    danger: '!',
  };
  const iconBg = {
    info: 'bg-[var(--dash-brand)]',
    success: 'bg-[var(--dash-success)]',
    warn: 'bg-amber-600',
    danger: 'bg-red-600',
  };

  return (
    <div
      role="status"
      className={`mb-alert relative flex gap-3 rounded-2xl border px-3.5 py-3 text-left ${map[tone]}`}
    >
      <span
        className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white ${iconBg[tone]}`}
        aria-hidden
      >
        {icon[tone]}
      </span>
      <div className="min-w-0 flex-1">
        <p className="m-0 text-sm font-semibold">{title}</p>
        {children ? (
          <div className="mt-1 text-xs leading-relaxed opacity-90">{children}</div>
        ) : null}
        {actions ? (
          <div className="mt-2.5 flex flex-wrap gap-2">{actions}</div>
        ) : null}
      </div>
      {onDismiss ? (
        <button
          type="button"
          aria-label="Dismiss"
          onClick={onDismiss}
          className="shrink-0 rounded-md p-1 opacity-60 transition hover:opacity-100"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </div>
  );
}

export function MbSkeleton({ className = '' }: { className?: string }) {
  return (
    <div
      className={`mb-skeleton animate-pulse rounded-xl bg-[var(--dash-line)] ${className}`}
      aria-hidden
    />
  );
}

export function MbEmptyState({
  title,
  description,
  action,
  icon,
}: {
  title: string;
  description: string;
  action?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-[var(--dash-line)] bg-[var(--dash-soft)] px-6 py-10 text-center">
      {icon ? <div className="text-[var(--dash-muted)]">{icon}</div> : null}
      <p className="m-0 text-sm font-semibold text-[var(--dash-ink)]">{title}</p>
      <p className="m-0 max-w-sm text-xs leading-relaxed text-[var(--dash-muted)]">
        {description}
      </p>
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}
